import { useEffect, useState } from 'preact/hooks';

/**
 * A counter that increments whenever the app comes back to the
 * foreground, for effects that need to re-ask the server rather than
 * trust what is already on screen.
 *
 * Why this exists: every date decision in the hub is made server-side and
 * frozen into the payload — `is_today` on the week grid, the digest's
 * day window, a deadline's "Vandaag"/"Morgen" chip. An installed PWA is
 * routinely left open for days, so a screen loaded yesterday keeps
 * rendering yesterday's answer under a heading that says "Vandaag". The
 * mutation-version signals (`tasksVersion`, `scheduleVersion`,
 * `listsVersion`) cannot catch this: nothing mutated — the calendar just
 * moved on.
 *
 * Both events are listened for because they answer different questions:
 * `visibilitychange` covers switching apps or unlocking the phone, and
 * `focus` covers returning to a background browser tab on desktop, which
 * does not always fire a visibility change. A quick alt-tab therefore
 * bumps this more than once; every consumer's effect is an idempotent
 * GET, so that costs a request, never correctness.
 *
 * Follows the same pattern UpcomingPanel (WP-2) already used inline —
 * hoisted here once a fourth and fifth consumer appeared.
 */
export function useVisibleRefresh() {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    function bump() {
      if (document.visibilityState === 'hidden') return;
      setTick((n) => n + 1);
    }

    window.addEventListener('focus', bump);
    document.addEventListener('visibilitychange', bump);
    return () => {
      window.removeEventListener('focus', bump);
      document.removeEventListener('visibilitychange', bump);
    };
  }, []);

  return tick;
}
